import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ApiCallError } from '@/lib/api';
import { formatCprDisplay } from '@/lib/cpr';
import { useRevealReferralCpr, type ReferralCprSubject } from '@/hooks/useReferrals';

/**
 * CPR on a referral. With `autoReveal`, the full number is fetched when the
 * detail opens (decrypt-referral-cpr, audited). Overviews/lists omit autoReveal
 * and keep the manual reveal control.
 */
export function ReferralCprBadge({
  referralId,
  subject,
  canReveal,
  replacement = false,
  autoReveal = false,
}: {
  referralId: string;
  subject: ReferralCprSubject;
  canReveal: boolean;
  /** The letter sent an erstatnings-CPR (AlternativeIdentifier), REF06-M 3.12. */
  replacement?: boolean;
  /** DIS01-M / REF06: show full CPR on opened referral detail without a click. */
  autoReveal?: boolean;
}) {
  const { t } = useTranslation();
  const reveal = useRevealReferralCpr();
  const [cpr, setCpr] = useState<string | null>(null);
  const [autoFailed, setAutoFailed] = useState(false);

  useEffect(() => {
    setCpr(null);
    setAutoFailed(false);
  }, [referralId, subject]);

  useEffect(() => {
    if (!autoReveal || !canReveal || cpr) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await reveal.mutateAsync({
          referral_id: referralId,
          subject,
          source: 'referral-detail',
          purpose: 'referral_detail',
        });
        if (!cancelled) setCpr(r.cpr);
      } catch (err) {
        if (!cancelled) {
          setAutoFailed(true);
          toast.error(err instanceof ApiCallError ? err.message : t('patients.errors.unknown'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reveal once per referral/subject when detail opens
  }, [autoReveal, canReveal, cpr, referralId, subject, t]);

  const onToggle = async () => {
    if (cpr) {
      setCpr(null);
      return;
    }
    try {
      const r = await reveal.mutateAsync({
        referral_id: referralId,
        subject,
        source: 'referral-detail',
        purpose: 'referral_detail',
      });
      setCpr(r.cpr);
      toast.success(t('patients.toast_cpr_revealed'));
    } catch (err) {
      toast.error(err instanceof ApiCallError ? err.message : t('patients.errors.unknown'));
    }
  };

  const placeholder = replacement ? t('referrals.replacement_cpr_present') : t('referrals.cpr_present');
  const showManualReveal = canReveal && !autoReveal;
  const pending = autoReveal ? reveal.isPending && !cpr && !autoFailed : reveal.isPending;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center rounded-full bg-ds-green-tint px-2 py-0.5 text-xs font-medium text-ds-green-ink">
        {autoReveal && pending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />}
        {cpr ? <span className="font-mono">{formatCprDisplay(cpr)}</span> : placeholder}
      </div>
      {replacement && (
        <span
          className="rounded bg-ds-amber-tint px-1.5 py-0.5 text-xs font-medium text-ds-amber-ink"
          data-testid={`referral-replacement-cpr-${subject}`}
        >
          {t('referrals.replacement_cpr')}
        </span>
      )}
      {autoReveal && !canReveal && (
        <span className="text-xs text-ds-muted">{t('referrals.cpr_no_permission', 'Du har ikke adgang til at se CPR-nummeret')}</span>
      )}
      {showManualReveal && (
        <button
          type="button"
          onClick={onToggle}
          disabled={reveal.isPending}
          className="inline-flex items-center gap-1 text-xs font-medium text-ds-teal hover:text-ds-teal disabled:opacity-50"
          data-testid={`referral-cpr-reveal-${subject}`}
        >
          {reveal.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : cpr ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
          {cpr ? t('patients.cpr_hide') : t('patients.cpr_reveal')}
        </button>
      )}
    </div>
  );
}
